import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Play, Pause, SkipForward, ExternalLink, Volume2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import type { Track, Playlist } from '@/services/spotifyService';
import { openSpotify } from '@/services/spotifyService';

interface SpotifyPreviewPlayerProps {
  playlist: Playlist;
  className?: string;
  onClose?: () => void;
}

const SpotifyPreviewPlayer: React.FC<SpotifyPreviewPlayerProps> = ({
  playlist,
  className,
  onClose
}) => {
  const { resolvedTheme } = useTheme();
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  const bgColor = resolvedTheme === 'dark' ? 'bg-black/95' : 'bg-white/95';

  const availableTracks = playlist.tracks?.filter(track => track.previewUrl) || [];
  const currentTrack = availableTracks[currentTrackIndex];

  useEffect(() => {
    if (isPlaying && audioRef.current) {
      progressInterval.current = setInterval(() => {
        const audio = audioRef.current;
        if (audio && audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      }, 100);
    } else {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying]);

  const handlePlayPause = () => {
    if (!currentTrack?.previewUrl || !audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (currentTrackIndex < availableTracks.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
      setProgress(0);
      setIsPlaying(false);
    }
  };

  const handleAudioEnd = () => {
    if (currentTrackIndex < availableTracks.length - 1) {
      handleNext();
    } else {
      setIsPlaying(false);
      setProgress(0);
    }
  };

  const handleOpenSpotify = () => {
    openSpotify(playlist.spotifyUrl);
    onClose?.();
  };

  if (!availableTracks.length) {
    return (
      <div className={cn('p-6 rounded-lg border backdrop-blur-sm', bgColor, className)}>
        <div className="text-center">
          <h3 className={cn('font-normal mb-2', textColor)}>{playlist.name}</h3>
          <p className={cn('text-sm mb-4', mutedTextColor)}>No preview available for this playlist</p>
          <Button onClick={handleOpenSpotify} className="bg-green-600 hover:bg-green-700 text-white">
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in Spotify
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('p-6 rounded-lg border backdrop-blur-sm', bgColor, className)}>
      {currentTrack && (
        <audio
          ref={audioRef}
          src={currentTrack.previewUrl || undefined}
          onEnded={handleAudioEnd}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      )}

      <div className="flex items-center gap-4 mb-4">
        <img
          src={playlist.imageUrl}
          alt={playlist.name}
          className="w-16 h-16 rounded-md object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h3 className={cn('font-semibold truncate', textColor)}>{playlist.name}</h3>
          {currentTrack && (
            <div className={cn('text-sm truncate', mutedTextColor)}>
              <span className="font-medium">{currentTrack.name}</span>
              <span className="mx-1">•</span>
              <span>{currentTrack.artist}</span>
            </div>
          )}
          <div className={cn('text-xs mt-1', mutedTextColor)}>
            Track {currentTrackIndex + 1} of {availableTracks.length}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
          <div
            className="bg-green-600 h-1 rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePlayPause}
            disabled={!currentTrack?.previewUrl}
            className="h-10 w-10 p-0"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            disabled={currentTrackIndex >= availableTracks.length - 1}
            className="h-10 w-10 p-0"
          >
            <SkipForward className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Volume2 className="w-3 h-3" />
            <span>30s preview</span>
          </div>
        </div>

        <Button
          onClick={handleOpenSpotify}
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Open in Spotify
        </Button>
      </div>
    </div>
  );
};

export default SpotifyPreviewPlayer;